import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPreferredCompanies } from '../../../api';
import { Building2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
const Companies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(sessionStorage.getItem('dashboard_search') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(sessionStorage.getItem('dashboard_page')) || 1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1
  });
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(sessionStorage.getItem('dashboard_rows')) || 25);
  const [openFilter, setOpenFilter] = useState(null);
  const filterClickCount = React.useRef(0);
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPreferredCompanies({
        page: currentPage,
        limit: rowsPerPage,
        search: searchTerm
      });
      setCompanies(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchTerm]);

  useEffect(() => {
    sessionStorage.setItem('dashboard_search', searchTerm);
    sessionStorage.setItem('dashboard_page', currentPage);
    sessionStorage.setItem('dashboard_rows', rowsPerPage);
  }, [searchTerm, currentPage, rowsPerPage]);
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCompanies();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchCompanies]);
  const handleSearch = e => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };
  const formatDate = dateStr => {
    if (!dateStr || dateStr === '—') return '—';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };
  const handleViewDetails = companyName => {
    navigate(`/all-companies/company-details/${encodeURIComponent(companyName)}`);
  };
  return <>
      <div className="topbar">
        <div className="topbar-left">
          <div className="topbar-title">All Companies</div>
          <div className="topbar-sub">{pagination.total} organizations monitored</div>
        </div>
        <div className="topbar-right">
        </div>
      </div>

      <div className="body">
        <div className="table-area companies-style-1">
          <div className="filters-bar">
            <div className="search-wrap">
              <Search size={16} className="search-icon" />
              <input className="search-input" type="text" placeholder="Search companies..." value={searchTerm} onChange={handleSearch} />
            </div>
            <div className="filters-right">
              <div className="filter-count">
                Showing {companies.length} of {pagination.total} companies
              </div>
            </div>
          </div>

          {loading ? <div className="loading companies-style-2">
              Loading preferences...
            </div> : <div className="filings-grid companies-style-3">
              {companies.map(c => <div key={c.company_name} className="filing-card" onClick={() => handleViewDetails(c.company_name)}>
                  <div className="card-header">
                    <span className="card-tn companies-style-4">
                      <Building2 size={14} color="var(--text2)" />
                    </span>
                    {c.openFilings > 0 && <span className="pill pill-amber">{c.openFilings} Open</span>}
                  </div>

                  <div className="card-company companies-style-5">
                    {c.company_name}
                  </div>

                  <div className="card-body">
                    <div className="field-grid companies-style-6">
                      <div className="field">
                        <div className="field-label">Total Filings</div>
                        <div className="field-val">{c.totalFilings}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">Recent Changes</div>
                        <div className="field-val">
                          {c.recentChanges > 0 ? <span className="pill pill-blue companies-style-7">
                              {c.recentChanges} new
                            </span> : <span className="companies-style-8">0</span>}
                        </div>
                      </div>
                      <div className="field">
                        <div className="field-label">Open Filings</div>
                        <div className="field-val">{c.openFilings}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">Last Updated</div>
                        <div className="field-val">{formatDate(c.lastUpdated)}</div>
                      </div>
                    </div>
                  </div>

                </div>)}
            </div>}

          {/* Pagination */}
          <div className="pagination">
            <div className="pg-info">
              Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, pagination.total)} of {pagination.total} companies
            </div>
            <div className="table-pagination">
              <div className="table-pagination__control">
                <span className="table-pagination__label">Rows per page</span>
                <div className={`select-wrap ${openFilter === 'rows' ? 'is-open' : ''}`}>
                  <span className="select-measure table-pagination__measure">{rowsPerPage}</span>
                  <select className="filter-select table-pagination__select" value={rowsPerPage} onFocus={() => {
                  filterClickCount.current = 0;
                  setOpenFilter('rows');
                }} onBlur={() => {
                  filterClickCount.current = 0;
                  setOpenFilter(null);
                }} onClick={e => {
                  filterClickCount.current++;
                  if (filterClickCount.current > 1) {
                    e.target.blur();
                  }
                }} onChange={e => {
                  const newLimit = parseInt(e.target.value);
                  setRowsPerPage(newLimit);
                  sessionStorage.setItem('dashboard_rows', newLimit);
                  setCurrentPage(1);
                  e.target.blur();
                }}>
                    <option value={25} className="table-pagination__option">25</option>
                    <option value={35} className="table-pagination__option">35</option>
                    <option value={50} className="table-pagination__option">50</option>
                  </select>
                </div>
              </div>
              <div className="pg-controls">
                <button className="pg-btn" disabled={currentPage === 1 || loading} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                  <ChevronLeft size={14} />
                </button>
                <div className="pagination__pages">
                  {[...Array(pagination.totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  if (pageNum === 1 || pageNum === pagination.totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                    return <button key={pageNum} className={`pg-btn ${currentPage === pageNum ? 'active' : ''}`} onClick={() => setCurrentPage(pageNum)}>
                          {pageNum}
                        </button>;
                  }
                  if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="pagination__ellipsis">…</span>;
                  }
                  return null;
                })}
                </div>
                <button className="pg-btn" disabled={currentPage === pagination.totalPages || loading} onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
};
export default Companies;